/**
 * The server-backed `DashboardRepository`, and the one-time import that fills it.
 *
 * Phase 6b of `docs/plans/postgresql.md`. The interface has been async since dashboards were
 * written, so this is the swap it was written for: `dashboard-state.ts` does not
 * change, and neither does anything that renders a tile.
 *
 * Three things this does that the `localStorage` one cannot:
 *
 * * **The list carries no documents.** `load()` returns rows marked `partial`,
 *   and `loadDashboard()` fetches one when it is opened. A browse list of two
 *   hundred dashboards costs one request and no `spec` at all.
 * * **Saves are versioned.** `saveDashboard()` sends the version it last read;
 *   a 409 is re-thrown as `DashboardConflictError` so the view can offer reload
 *   or overwrite instead of one arrangement silently winning.
 * * **Failures are loud.** Every method throws. A dashboard that did not save
 *   must not look saved - the opposite of the corrections path, where an outage
 *   is meant to be invisible.
 *
 * Which repository is in use is decided at boot by `chooseRepository()`:
 * the server when this account can reach stored dashboards, `localStorage`
 * otherwise. With no database the section behaves exactly as it did before any
 * of this existed, which is rule one of `CLAUDE.md`.
 */

import { ApiError } from '../../api/client.ts';
import {
  createDashboard,
  createDashboardFolder,
  deleteDashboard,
  deleteDashboardFolder,
  getDashboard,
  importDashboards,
  listDashboards,
  renameDashboardFolder,
  saveDashboard as saveDashboardRequest,
} from '../../api/endpoints.ts';
import type { DashboardFolderRow, DashboardRow } from '../../api/types.ts';
import { loadMe } from '../../state/me.ts';
import { canDo, catalogAvailable, myUserId } from '../../state/store.ts';
import {
  DASHBOARDS_KEY,
  SCHEMA_VERSION,
  dashboardDirty,
  loadDashboards,
  localDashboardRepository,
  normalizeSettings,
  setDashboardRepository,
  type Dashboard,
  type DashboardFolder,
  type DashboardRepository,
  type DashboardStore,
} from './dashboard-state.ts';
import { defaultVariables } from './variables.ts';

/** Marks the browser store as handed over, so the import runs once per account. */
export const IMPORT_DONE_KEY = 'neptune_dashboards_imported_v1';

/** Somebody else saved this dashboard since it was opened. */
export class DashboardConflictError extends Error {
  readonly currentVersion: number | null;
  constructor(currentVersion: number | null) {
    super('This dashboard was saved by someone else since you opened it.');
    this.name = 'DashboardConflictError';
    this.currentVersion = currentVersion;
  }
}

function toDashboard(row: DashboardRow, partial: boolean): Dashboard {
  const spec = (row.spec ?? {}) as Partial<Dashboard> & { variables?: unknown };
  return {
    id: row.id,
    name: row.title,
    folderId: row.folder_id,
    variables: Array.isArray(spec.variables) && spec.variables.length
      ? (spec.variables as Dashboard['variables'])
      : defaultVariables(),
    tabs: Array.isArray(spec.tabs) ? (spec.tabs as Dashboard['tabs']) : [],
    // Over the defaults: a document written before a setting existed - or by
    // the API without one - reads that setting as its default, not as off.
    settings: normalizeSettings(spec.settings),
    filters: Array.isArray(spec.filters) ? spec.filters : [],
    schemaVersion: row.schema_version || SCHEMA_VERSION,
    updatedAt: row.updated_at,
    version: row.version,
    access: {
      isOwner: row.is_owner,
      canEdit: row.can_edit,
      sharedDirectly: row.shared_directly,
      shareCount: row.share_count ?? null,
      ownerName: row.owner?.display_name ?? null,
      ownerEmail: row.owner?.email ?? null,
    },
    ...(partial ? { partial: true } : {}),
    ...(row.tile_count != null && row.tab_count != null
      ? { counts: { tiles: row.tile_count, tabs: row.tab_count } }
      : {}),
  };
}

function toFolder(row: DashboardFolderRow): DashboardFolder {
  return { id: row.id, name: row.name, createdAt: row.created_at };
}

/** The document half of a dashboard - what goes in `spec`. */
function toSpec(dashboard: Dashboard): Record<string, unknown> {
  return {
    schemaVersion: SCHEMA_VERSION,
    variables: dashboard.variables,
    settings: dashboard.settings,
    filters: dashboard.filters,
    tabs: dashboard.tabs,
  };
}

export const serverDashboardRepository: DashboardRepository = {
  async load(): Promise<DashboardStore> {
    const { dashboards, folders } = await listDashboards();
    return {
      folders: folders.map(toFolder),
      // Partial: the row without its document. Opening one fetches it.
      dashboards: dashboards.map((row) => toDashboard(row, true)),
    };
  },

  async loadDashboard(id: string): Promise<Dashboard | null> {
    const row = await getDashboard(id);
    return toDashboard(row, false);
  },

  async saveDashboard(dashboard: Dashboard, options: { force?: boolean } = {}): Promise<void> {
    // A **partial** dashboard is a browse-list row whose document was never
    // fetched - `tabs` is empty because nobody asked, not because it is empty.
    // Renaming or moving one from the list must send the metadata and nothing
    // else, or the write would replace a real document with an empty one.
    const body = {
      title: dashboard.name,
      ...(dashboard.partial ? {} : { spec: toSpec(dashboard) }),
      ...(dashboard.folderId === null ? { move_to_root: true } : { folder_id: dashboard.folderId }),
    };
    try {
      // A dashboard with no version has never been on the server: it was made
      // in this session, or by the local repository before the swap. One that
      // has is always a PUT - `force` only drops the version check, never turns
      // an overwrite into a second dashboard.
      const row = dashboard.version
        ? await saveDashboardRequest(dashboard.id, {
            ...body,
            ...(options.force ? {} : { version: dashboard.version }),
          })
        // A create always carries the document: there is nothing stored to
        // preserve, and the API requires one.
        : await createDashboard({ ...body, spec: body.spec ?? toSpec(dashboard) });
      // The id and version the server gave it, so the next save is judged
      // against the right row rather than creating a second one.
      dashboard.id = row.id;
      dashboard.version = row.version;
      dashboard.updatedAt = row.updated_at;
      // A dashboard just created here is its creator's, whole. Without this it
      // had no `access` until the list was reloaded, and the browse row - which
      // reads `access` to know the dashboard is stored - offered no Share.
      dashboard.access ??= {
        isOwner: true,
        canEdit: true,
        sharedDirectly: false,
        shareCount: 0,
        ownerName: null,
        ownerEmail: null,
      };
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        throw new DashboardConflictError(currentVersionFrom(e));
      }
      throw e;
    }
  },

  async removeDashboard(id: string): Promise<void> {
    await deleteDashboard(id);
  },

  async saveFolder(folder: DashboardFolder, options: { create?: boolean } = {}): Promise<void> {
    // The caller says which this is. Guessing from a 404 was wrong in both
    // directions: a rename that raced a delete would silently create a second
    // folder, and any other 404 would be swallowed as "must be new".
    const row = options.create
      ? await createDashboardFolder(folder.name)
      : await renameDashboardFolder(folder.id, folder.name);
    folder.id = row.id;
  },

  async removeFolder(id: string): Promise<void> {
    await deleteDashboardFolder(id);
  },
};

/** The version named in a 409 body, when the server managed to say. */
function currentVersionFrom(error: ApiError): number | null {
  const match = /current_version[^0-9]*([0-9]+)/.exec(error.message);
  return match?.[1] ? Number(match[1]) : null;
}

/**
 * Whether this account can keep dashboards on the server.
 *
 * `catalogAvailable` is the deployment's answer about stored state at all, and
 * `dashboards:write` is this account's. Without both, the browser store is not
 * a degraded mode - it is the app as it has always been.
 */
export function canUseServerDashboards(): boolean {
  return catalogAvailable.value && canDo('dashboards:write');
}

/**
 * Pick the repository for this session, and hand over the browser's dashboards
 * the first time the server is available.
 *
 * The local store is **not** deleted afterwards, only marked as imported: the
 * cost of keeping it is a few kilobytes, and it is the only copy if an import
 * turns out to have gone somewhere unexpected.
 */
export async function chooseRepository(): Promise<void> {
  // Wait for the boot request before deciding. `catalogAvailable` starts false
  // because nobody has asked yet, and a session that lands straight on
  // `?section=dashboards` gets here first: choosing on that default filed the
  // whole session in `localStorage`, where a dashboard has a browser-made id
  // and nothing can be shared. `loadMe` is one in-flight promise shared with
  // the shell, so this awaits the answer rather than asking again.
  await loadMe().catch(() => {
    // A boot request that failed is an answer too: nothing stored is reachable,
    // which is exactly the browser-store case below.
  });
  if (!canUseServerDashboards()) {
    setDashboardRepository(localDashboardRepository);
    return;
  }
  setDashboardRepository(serverDashboardRepository);
  await importLocalDashboards();
}

let loading: Promise<void> | null = null;
let loadedSource: string | null = null;

function sourceKey(): string {
  return canUseServerDashboards() ? `server:${myUserId.value ?? ''}` : 'local';
}

/**
 * Choose the repository and load the list, again if account access recovers.
 *
 * The section boots this, and the Assistant needs the same list before it can
 * name a dashboard - sharing one promise keeps the two from choosing twice or
 * importing the browser store twice.
 */
export function ensureDashboardsLoaded(): Promise<void> {
  // A temporary boot failure must not pin this page to the browser store.
  // Preserve an unsaved document until it is saved or discarded.
  if (loadedSource !== null && loadedSource !== sourceKey() && !dashboardDirty.value) {
    loading = null;
    loadedSource = null;
  }
  loading ??= (async () => {
    await chooseRepository();
    const source = sourceKey();
    await loadDashboards();
    loadedSource = source;
  })().catch((e) => {
    loading = null;
    throw e;
  });
  return loading;
}

/** Hand the browser store to the account, once. Never fatal: this is a bonus. */
export async function importLocalDashboards(): Promise<number> {
  let raw: string | null = null;
  try {
    if (localStorage.getItem(IMPORT_DONE_KEY)) return 0;
    raw = localStorage.getItem(DASHBOARDS_KEY);
  } catch {
    return 0;
  }
  if (!raw) return 0;

  try {
    const store = await localDashboardRepository.load();
    if (!store.dashboards.length) {
      markImported();
      return 0;
    }
    const folderNames = new Map(store.folders.map((f) => [f.id, f.name]));
    const result = await importDashboards({
      folders: store.folders.map((f) => ({ name: f.name })),
      dashboards: store.dashboards.map((d) => ({
        title: d.name,
        spec: toSpec(d),
        // Folders are matched by name on the server: the local ids mean nothing
        // to it, and two people importing "Engines" should not collide.
        folder_id: d.folderId ? (folderNames.get(d.folderId) ?? null) : null,
      })),
    });
    markImported();
    return result.dashboards.length;
  } catch (e) {
    // Leave the marker unset so the next load tries again, and leave the local
    // store alone. Nothing here is worth blocking the section for.
    console.warn('Could not import local dashboards', e);
    return 0;
  }
}

function markImported(): void {
  try {
    localStorage.setItem(IMPORT_DONE_KEY, new Date().toISOString());
  } catch {
    /* ignore - it just means the import is attempted again next time */
  }
}
